import { useState, useEffect } from 'react';
import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import { Home, UserPlus, CalendarDays, Users, Shield, Phone, Download, X } from 'lucide-react';
import MobileHome from './MobileHome';
import MobileParent from './MobileParent';
import MobileAdmin from './MobileAdmin';
import MobileRegister from './MobileRegister';
import MobileTour from './MobileTour';
import MobileContact from './MobileContact';
import MobileIntro from './MobileIntro';

const navItems = [
  { to: '/mobile', icon: Home, label: 'Home', end: true },
  { to: '/mobile/register', icon: UserPlus, label: 'Enroll' },
  { to: '/mobile/tour', icon: CalendarDays, label: 'Tour' },
  { to: '/mobile/parent', icon: Users, label: 'Parent' },
  { to: '/mobile/contact', icon: Phone, label: 'Contact' },
];

const MobileApp = () => {
  const [showIntro, setShowIntro] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);

  // Intro animation - once per session
  useEffect(() => {
    const seen = sessionStorage.getItem('mobileIntroSeen');
    if (!seen) setShowIntro(true);
  }, []);

  const handleIntroComplete = () => {
    setShowIntro(false);
    sessionStorage.setItem('mobileIntroSeen', 'true');
  };

  // PWA install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      const dismissed = localStorage.getItem('installDismissed');
      if (!dismissed) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowInstallBanner(false);
      setInstallPrompt(null);
    }
  };

  const dismissInstall = () => {
    setShowInstallBanner(false);
    localStorage.setItem('installDismissed', 'true');
  };

  return (
    <div className="min-h-screen bg-background flex flex-col max-w-lg mx-auto relative" data-testid="mobile-app-container">
      {/* Intro Animation */}
      {showIntro && <MobileIntro onComplete={handleIntroComplete} />}

      {/* Install Banner */}
      {showInstallBanner && (
        <div className="sticky top-0 z-50 bg-secondary text-white px-4 py-3 flex items-center gap-3" data-testid="mobile-install-banner">
          <Download className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold">Add to Home Screen</p>
            <p className="text-xs opacity-80 truncate">Install Hooyo's Place for quick access</p>
          </div>
          <button onClick={handleInstall} className="bg-white text-secondary px-3 py-1.5 rounded-lg text-xs font-bold flex-shrink-0" data-testid="mobile-install-btn">
            Install
          </button>
          <button onClick={dismissInstall} className="text-white/70 flex-shrink-0" data-testid="mobile-install-dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border/20 px-4 py-3 flex items-center justify-between">
        <span className="font-fredoka font-bold text-sm">
          <span className="text-primary">Hoo</span><span className="text-secondary">yo's</span><span className="text-accent"> Place</span>
        </span>
        {/* Admin shortcut */}
        <NavLink to="/mobile/admin" className={({ isActive }) => `p-1.5 rounded-lg transition-colors ${isActive ? 'bg-primary/10' : 'hover:bg-muted/50'}`} data-testid="mobile-admin-shortcut">
          <Shield className="w-4 h-4 text-muted-foreground" />
        </NavLink>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto pb-20">
        <Routes>
          <Route index element={<MobileHome />} />
          <Route path="parent" element={<MobileParent />} />
          <Route path="admin" element={<MobileAdmin />} />
          <Route path="register" element={<MobileRegister />} />
          <Route path="tour" element={<MobileTour />} />
          <Route path="contact" element={<MobileContact />} />
          <Route path="*" element={<Navigate to="/mobile" replace />} />
        </Routes>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-t border-border/30 safe-area-bottom max-w-lg mx-auto" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around py-2 px-1">
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to} to={to} end={end}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all min-w-[56px] ${isActive ? 'text-secondary' : 'text-muted-foreground/60'}`}
              data-testid={`mobile-nav-${label.toLowerCase()}`}
            >
              {({ isActive }) => (
                <>
                  <div className={`p-1.5 rounded-xl transition-all ${isActive ? 'bg-secondary/10' : ''}`}>
                    <Icon className={`w-5 h-5 transition-all ${isActive ? 'text-secondary' : ''}`} />
                  </div>
                  <span className={`text-[10px] font-semibold ${isActive ? 'text-secondary' : ''}`}>{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default MobileApp;
